import { FiVolume2 } from 'react-icons/fi';

export const pronunciationData = {
  courseId: 'pronunciation',
  courseType: 'speaking-course',
  title: 'Pronunciation',
  icon: FiVolume2,
  overview: {
    impact: 'Be understood clearly',
    focus: 'Sounds, stress, and intonation',
    uses: 'All Speaking parts',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Pronunciation in IELTS',
      content: `<p>Pronunciation is worth 25% of your Speaking score. You're assessed on:</p>`,
      points: [
        'Individual sounds (consonants and vowels)',
        'Word stress (which syllable is emphasized)',
        'Sentence stress (which words are emphasized)',
        'Intonation (rise and fall of voice)',
        'Connected speech (how words link together)',
      ],
      warning: 'You don\'t need a "native" accent! Clarity and intelligibility are what matter.',
    },
    {
      title: 'Common Problem Sounds',
      content: `<p>Many learners struggle with these English sounds:</p>`,
      table: {
        headers: ['Sound', 'Example Words', 'Tip'],
        rows: [
          ['/θ/ (th)', 'think, three, through', 'Tongue between teeth, blow air'],
          ['/ð/ (th)', 'this, that, the', 'Like /θ/ but with voice'],
          ['/r/', 'red, right, around', 'Tongue curled back, doesn\'t touch'],
          ['/l/', 'like, feel, play', 'Tongue touches roof of mouth'],
          ['/v/ vs /w/', 'very vs. wery', '/v/: teeth on lip; /w/: lips rounded'],
        ],
      },
    },
    {
      title: 'Word Stress',
      content: `<p>English words have stressed syllables. Getting stress wrong can cause confusion:</p>`,
      examples: [
        { label: 'Correct', text: 'phoTOgraph, photoGRAPHic, phoTOgrapher' },
        { label: 'Pattern', text: 'Different forms of the word have different stress' },
      ],
      points: [
        'Two-syllable nouns: usually stress first syllable (TAble, DOCtor)',
        'Two-syllable verbs: often stress second syllable (reLAX, deCIDE)',
        'Suffixes like -tion, -sion: stress the syllable before (eduCAtion)',
      ],
    },
    {
      title: 'Sentence Stress & Intonation',
      content: `<p>Which words do you stress in a sentence?</p>`,
      points: [
        '<strong>Content words</strong> are stressed: nouns, verbs, adjectives, adverbs',
        '<strong>Function words</strong> are usually unstressed: articles, prepositions',
        'Rising intonation: questions, uncertainty',
        'Falling intonation: statements, certainty',
      ],
      examples: [
        { 
          label: 'Stressed words', 
          text: '"I THINK TECHNOLOGY is REALLY IMPORTANT in MODERN LIFE."' 
        },
        { 
          label: 'Question', 
          text: '"Do you like traveling? ↗" (rising at end)' 
        },
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify the Stress',
      type: 'select',
      instructions: 'Which syllable is stressed in each word?',
      questions: [
        {
          text: 'education',
          options: ['e-du-CA-tion', 'ED-u-ca-tion', 'e-DU-ca-tion'],
          answer: 'e-du-CA-tion',
          explanation: 'Stress falls on the syllable before -tion.',
        },
        {
          text: 'photograph',
          options: ['PHO-to-graph', 'pho-TO-graph', 'pho-to-GRAPH'],
          answer: 'PHO-to-graph',
          explanation: 'First syllable is stressed in "photograph".',
        },
        {
          text: 'important',
          options: ['im-POR-tant', 'IM-por-tant', 'im-por-TANT'],
          answer: 'im-POR-tant',
          explanation: 'Second syllable is stressed.',
        },
      ],
    },
    {
      title: 'Practice Problem Sounds',
      type: 'writing',
      instructions: 'Write sentences using these sounds, then practice saying them.',
      questions: [
        {
          text: 'Create a sentence with multiple /θ/ (th) sounds.',
          example: 'I think that three things are worth thinking through thoroughly.',
          tip: 'Practice putting your tongue between your teeth.',
        },
        {
          text: 'Create a sentence comparing /v/ and /w/ sounds.',
          example: 'The very wise woman viewed the waves while walking westward.',
          tip: 'For /v/, feel your teeth on your lip.',
        },
      ],
    },
  ],
  tips: [
    'Listen and imitate native speakers',
    'Record yourself and compare to native speakers',
    'Focus on clarity, not accent elimination',
    'Practice problem sounds daily',
  ],
  dailyPractice: [
    'Shadow English speakers (repeat immediately after)',
    'Practice minimal pairs (ship/sheep, rice/lice)',
    'Record yourself reading aloud',
    'Use speech recognition to check clarity',
  ],
  backPath: '/exams/english/#speaking',
  backLabel: 'Back to Speaking',
  aiContext: 'Pronunciation: Clear pronunciation including individual sounds, word stress, sentence stress, and intonation for IELTS Speaking.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...pronunciationData,
      title: pronunciationData.title + ' (Foundations)',
      overview: {
        ...pronunciationData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: pronunciationData.sections.slice(0, Math.max(2, Math.ceil(pronunciationData.sections.length / 2))),
      exercises: pronunciationData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...pronunciationData,
      title: pronunciationData.title + ' (Advanced)',
      overview: {
        ...pronunciationData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: pronunciationData.sections,
      exercises: pronunciationData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(pronunciationData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return pronunciationData;
}
